import React, { useMemo } from 'react';
import { stageMeta, daysSince, fmtDate } from './medicalShared';

// Einstein compose panel for medical (2026-09-10) — same pattern as gps/MatchChronicPanel.js:
// presentational only, takes a player id plus the shared reference data (players/injuries/
// illnesses, fetched once for the whole compose response, not once per panel) and renders through
// the same stageMeta/daysSince helpers Medical.js itself uses, so a status badge here can never
// silently drift from what the full page would show. "Days since injury" (date_injured), not
// "days in current stage" — the current-stage-entry timestamp lives in injury_stage_history,
// which getInjuries() doesn't join in; showing the wrong precision would be worse than showing a
// slightly different (but honestly labeled) number. RECENT_LIMIT caps the history list the same
// way MatchChronicPanel.js caps its match rows.
const RECENT_LIMIT = 3;

function currentStatus(playerId, injuries, illnesses) {
  const openInjury = injuries.find(i => String(i.player_id) === String(playerId) && !i.is_closed);
  if (openInjury) {
    const meta = stageMeta(openInjury.current_stage);
    return {
      kind: 'injury', color: meta.color,
      label: `${openInjury.body_part} — ${openInjury.injury_type}`,
      sub: `${meta.label} · ${daysSince(openInjury.date_injured)}d since injury`,
    };
  }
  const activeIllness = illnesses.find(i => String(i.player_id) === String(playerId) && i.status !== 'cleared');
  if (activeIllness) {
    return {
      kind: 'illness', color: '#f59e0b',
      label: activeIllness.illness_type || 'Illness',
      sub: `${activeIllness.status} · reported ${fmtDate(activeIllness.date_reported)}`,
    };
  }
  return { kind: 'available', color: '#10b981', label: 'Fully available', sub: null };
}

export default function MedicalPanel({ playerId, players, injuries, illnesses, limit = RECENT_LIMIT }) {
  const player = useMemo(() => players.find(p => String(p.id) === String(playerId)), [players, playerId]);

  const status = useMemo(
    () => player ? currentStatus(playerId, injuries, illnesses) : null,
    [playerId, injuries, illnesses, player]
  );

  // Closed injuries + cleared illnesses, most recent first — a quick history, not the full log.
  const recent = useMemo(() => {
    if (!player) return [];
    const closedInjuries = injuries
      .filter(i => String(i.player_id) === String(playerId) && i.is_closed)
      .map(i => ({ date: i.date_injured, label: `${i.body_part} — ${i.injury_type}`, resolved: i.actual_return_date }));
    const clearedIllnesses = illnesses
      .filter(i => String(i.player_id) === String(playerId) && i.status === 'cleared')
      .map(i => ({ date: i.date_reported, label: i.illness_type || 'Illness', resolved: i.actual_return_date }));
    return [...closedInjuries, ...clearedIllnesses]
      .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
      .slice(0, limit);
  }, [injuries, illnesses, playerId, player, limit]);

  if (!player) {
    return <div style={{ padding: 16, color: 'var(--text-dimmed)', fontSize: 13 }}>No player found for id {playerId}.</div>;
  }

  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '18px 20px', minWidth: 280 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 2 }}>{player.name}</div>
      <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginBottom: 14 }}>Medical status</div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <span style={{ width: 8, height: 8, borderRadius: 999, background: status.color, flex: 'none' }} />
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{status.label}</div>
          {status.sub && <div style={{ fontSize: 11, color: 'var(--text-dimmed)' }}>{status.sub}</div>}
        </div>
      </div>

      <div style={{ fontSize: 10, color: 'var(--text-dimmed)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
        Recent history
      </div>
      {recent.length === 0 ? (
        <div style={{ color: 'var(--text-dimmed)', fontSize: 12 }}>No closed injuries or illnesses on record.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {recent.map((r, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
              <span style={{ color: 'var(--text-secondary)' }}>{r.label}</span>
              <span style={{ color: 'var(--text-muted)' }}>{fmtDate(r.date)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
